'use client';

import { useEffect } from 'react';
import * as Sentry from '@sentry/react';

let initialized = false;
const SENSITIVE_KEY = /authorization|cookie|password|secret|token|email|phone|address/i;

function redactUnknown(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(redactUnknown);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      SENSITIVE_KEY.test(key) ? '[redacted]' : redactUnknown(item),
    ]),
  );
}

export function SentryClientInit(): null {
  useEffect(() => {
    if (initialized) return;

    const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN?.trim();
    if (!dsn) return;

    Sentry.init({
      dsn,
      environment:
        process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT?.trim() ||
        process.env.NODE_ENV ||
        'development',
      release: process.env.NEXT_PUBLIC_SENTRY_RELEASE?.trim(),
      sendDefaultPii: false,
      tracesSampleRate: Number(process.env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE ?? '0.1'),
      beforeSend(event) {
        event.request = event.request
          ? {
              ...event.request,
              cookies: undefined,
              data: redactUnknown(event.request.data),
              headers: redactUnknown(event.request.headers) as Record<string, string>,
              query_string: undefined,
              url: event.request.url?.split('?')[0],
            }
          : undefined;
        event.user = event.user?.id ? { id: String(event.user.id) } : undefined;
        event.extra = redactUnknown(event.extra) as typeof event.extra;
        return event;
      },
    });
    initialized = true;
  }, []);

  return null;
}

export { Sentry };
